import { formatDistanceToNow } from 'date-fns'
import { BetsCycleLoader } from '@/components/ui/betscycle-loader'
import { P2pUserAvatar } from '@/components/p2p/detail/P2pUserAvatar'
import { P2P_ACTIVITIES_PER_PAGE } from '@/config/p2p'
import { formatAmount } from '@/lib/format-number'
import { cn } from '@/lib/utils'
import { useGetP2pActivitiesQuery } from '@/store/api/p2pApi'

interface P2pActivityProps {
	marketId: string
	optionId: string
}

export function P2pActivity({ marketId, optionId }: P2pActivityProps) {
	const { data, isLoading, isError } = useGetP2pActivitiesQuery({
		market_id: marketId,
		option_id: optionId,
		page: 1,
		per_page: P2P_ACTIVITIES_PER_PAGE,
	})

	const accentClass = (side: 'yes' | 'no') => (side === 'yes' ? 'text-success' : 'text-destructive')

	if (isLoading) {
		return (
			<div className='flex min-h-[160px] items-center justify-center'>
				<BetsCycleLoader size={32} aria-label='Loading activity' />
			</div>
		)
	}

	if (isError) {
		return <p className='py-4 text-center text-sm text-muted-foreground'>Unable to load activity right now.</p>
	}

	const activity = data?.data ?? []

	if (activity.length === 0) {
		return <p className='py-4 text-center text-sm text-muted-foreground'>No activity yet.</p>
	}

	return (
		<div className='w-full overflow-hidden rounded-xl border border-border bg-background'>
			<div className='grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.6fr)_auto] gap-4 border-b border-border px-4 py-3 text-sm font-medium tracking-[-0.21px] text-muted-foreground sm:px-5'>
				<span>User</span>
				<span>Actions</span>
				<span className='text-right'>Time</span>
			</div>

			<div className='divide-y divide-border'>
				{activity.map((item) => {
					const username = item.player?.username?.trim() || 'Anonymous'
					const side = item.option_type
					const shares = Number(item.shares)
					const price = Number(item.usd_amount)

					return (
						<div key={item.id} className='grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.6fr)_auto] items-center gap-4 px-4 py-3.5 sm:px-5'>
							<div className='flex min-w-0 items-center gap-2.5'>
								<P2pUserAvatar username={username} avatarUrl={item.player?.avatar_url} />
								<span className='truncate text-sm font-medium tracking-[-0.21px] text-foreground'>{username}</span>
							</div>

							<p className='text-sm tracking-[-0.21px] text-muted-foreground'>
								Bought{' '}
								<span className={cn('font-medium', accentClass(side))}>
									{formatAmount(shares)} {side === 'yes' ? 'Yes' : 'No'}
								</span>{' '}
								shares at <span className={cn('font-medium', accentClass(side))}>${formatAmount(price)}</span>
							</p>

							<span className='whitespace-nowrap text-right text-sm tracking-[-0.21px] text-muted-foreground'>
								{formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
							</span>
						</div>
					)
				})}
			</div>
		</div>
	)
}
